import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { PublicV2Service } from 'src/app/core/api-v2/public-v2.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { TokenService } from 'src/app/shared/services/token.service';

/** Where to continue after a successful verification. */
type AfterVerify = 'login' | 'app';

/**
 * In-app 6-digit e-mail verification code entry. Reachable in the public/auth
 * area (route `verify-email`). It no longer reads a token from the URL — the
 * e-mail is carried in via router state (post-registration and the login gate)
 * with a fallback to the logged-in user's e-mail from TokenService.
 *
 * `afterVerify` selects the onward navigation:
 *  - `login` (post-registration): go to `auth/login` so the user signs in.
 *  - `app`   (login gate, already authenticated): navigate to '' and let the
 *            AuthGuard + companySetup guard route to /setup or the dashboard.
 */
@Component({
  selector: 'app-verify-email',
  templateUrl: './verify-email.component.html',
  styleUrls: ['./verify-email.component.scss'],
})
export class VerifyEmailComponent implements OnInit {
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _publicV2 = inject(PublicV2Service);
  private _notification = inject(NotificationService);
  private _transloco = inject(TranslocoService);
  private _token = inject(TokenService);

  /** E-mail the code was sent to (shown in the subtext, used in requests). */
  public email = '';

  /** The 6-digit code the user types. */
  public code = '';

  public verifying = signal<boolean>(false);
  public resending = signal<boolean>(false);

  /** "Wrong e-mail?" correction form (audit follow-up: mistyped address = stuck account). */
  public changingEmail = signal<boolean>(false);
  public changeSubmitting = signal<boolean>(false);
  public newEmail = '';
  public password = '';

  private afterVerify: AfterVerify = 'login';

  ngOnInit(): void {
    const state = (history.state ?? {}) as {
      email?: string;
      afterVerify?: AfterVerify;
    };

    this.email =
      state.email ||
      this._route.snapshot.queryParamMap.get('email') ||
      this._token.getUser()?.email ||
      '';

    if (state.afterVerify === 'app' || state.afterVerify === 'login') {
      this.afterVerify = state.afterVerify;
    }
  }

  /**
   * Sanitizes the input to digits and auto-submits as soon as a full 6-digit
   * code is present (OTP UX) — the button remains as an explicit fallback.
   * Re-fires only on an actual change, and the `verifying()` guard blocks
   * double submission, so a failed code doesn't loop.
   */
  public onCodeChanged(): void {
    const digits = (this.code || '').replace(/\D/g, '').slice(0, 6);
    if (digits !== this.code) {
      this.code = digits;
    }
    if (digits.length === 6 && !this.verifying()) {
      this.verify();
    }
  }

  /** Confirm the entered code. */
  public verify(): void {
    const code = this.code?.trim();
    if (!this.email || !code || code.length !== 6 || this.verifying()) return;

    this.verifying.set(true);
    this._publicV2.verifyEmail(this.email, code).subscribe({
      next: (result) => {
        this.verifying.set(false);
        if (!result?.verified) return;

        this._notification.success(
          this._transloco.translate('verifyEmail.verifySuccess')
        );

        if (this.afterVerify === 'app') {
          // Already authenticated (login gate): resume the normal post-login
          // navigation — the guards route to /setup when the wizard is pending.
          this._router.navigate(['']);
        } else {
          // Carry the verified e-mail along so the login form is prefilled (audit F-04).
          this._router.navigate(['/auth/login'], {
            state: { email: this.email },
          });
        }
      },
      // Wrong/expired code (error.emailVerification.invalidToken) is surfaced
      // by the global ErrorService; nothing else to do here.
      error: () => this.verifying.set(false),
    });
  }

  /** Request a fresh code for the same e-mail. */
  public resend(): void {
    if (!this.email || this.resending()) return;

    this.resending.set(true);
    this._publicV2.resendVerification(this.email).subscribe({
      next: () => {
        this.resending.set(false);
        this._notification.success(
          this._transloco.translate('verifyEmail.resendSuccess')
        );
      },
      error: () => this.resending.set(false),
    });
  }

  /** Toggle the "wrong e-mail?" correction form. */
  public toggleChangeEmail(open: boolean): void {
    this.changingEmail.set(open);
    if (!open) {
      this.newEmail = '';
      this.password = '';
    }
  }

  /**
   * Swap the unverified account's e-mail (password proves ownership) and send
   * the code to the corrected address. On success the screen simply continues
   * with the new address. Credential errors (error.emailVerification.invalidToken)
   * and a taken address (error.unique.userEmail) are surfaced by the ErrorService.
   */
  public changeEmail(): void {
    const newEmail = this.newEmail?.trim().toLowerCase();
    if (!this.email || !newEmail || !this.password || this.changeSubmitting()) {
      return;
    }

    this.changeSubmitting.set(true);
    this._publicV2
      .changeVerificationEmail(this.email, this.password, newEmail)
      .subscribe({
        next: () => {
          this.changeSubmitting.set(false);
          this.email = newEmail;
          this.code = '';
          this.toggleChangeEmail(false);
          this._notification.success(
            this._transloco.translate('verifyEmail.changeSuccess', {
              email: newEmail,
            })
          );
        },
        error: () => this.changeSubmitting.set(false),
      });
  }
}
